import React from 'react';
import {
    Grid,
    Box,
    TextField,
    FormControlLabel,
    Checkbox,
    Card,
    CardContent,
    Typography,
    Divider
} from "@mui/material";

const Aistages = ({ formData, setFormData, errors, setAistagesErrors }) => {

    // Helper function to convert string to boolean for checkboxes
    const getCheckboxValue = (value) => {
        if (typeof value === 'boolean') return value;
        if (value === 'true' || value === '1' || value === 1) return true;
        if (value === 'false' || value === '0' || value === 0 || value === null || value === undefined) return false;
        return Boolean(value);
    };

    // Word count function
    const getWordCount = (text) => {
        if (!text || text.trim() === '') return 0;
        return text.trim().split(/\s+/).length;
    };

    // Helper function to show word count status
    const getWordCountText = (text, fieldName) => {
        const wordCount = getWordCount(text);
        let color = 'textSecondary';
        let status = '';

        if (wordCount < 50) {
            color = 'error';
            status = ` (Minimum 50 words required - ${50 - wordCount} more needed)`;
        } else if (wordCount > 200) {
            color = 'error';
            status = ` (Maximum 200 words exceeded)`;
        } else {
            color = 'success';
            status = ` (${wordCount}/200 words)`;
        }

        return { text: status, color };
    };

    // Clear error when typing
    const clearError = (fieldName) => {
        if (errors && errors[fieldName]) {

            const newErrors = { ...errors };
            delete newErrors[fieldName];
            setAistagesErrors(newErrors);
        }
    };

    // Directly assign to formData - store as string to match your data format
    const handleConceptChange = (e) => {
        setFormData({
            ...formData,
            concept_script_development: e.target.checked.toString()
        });
        clearError('concept_script_development_description');
    };

    const handleConceptDescriptionChange = (e) => {
        setFormData({
            ...formData,
            concept_script_development_description: e.target.value
        });
        clearError('concept_script_development_description');
    };

    const handleStoryboardingChange = (e) => {
        setFormData({
            ...formData,
            storyboarding_pre_visualization: e.target.checked.toString()
        });
        clearError('storyboarding_pre_visualization_description');
    };

    const handleStoryboardingDescriptionChange = (e) => {
        setFormData({
            ...formData,
            storyboarding_pre_visualization_description: e.target.value
        });
        clearError('storyboarding_pre_visualization_description');
    };

    const handleVideoChange = (e) => {
        setFormData({
            ...formData,
            video_generation_animation: e.target.checked.toString()
        });
        clearError('video_generation_animation_description');
    };

    const handleVideoDescriptionChange = (e) => {

        setFormData({
            ...formData,
            video_generation_animation_description: e.target.value
        });
        clearError('video_generation_animation_description');
    };

    const handleEditingChange = (e) => {
        setFormData({
            ...formData,
            editing_post_production: e.target.checked.toString()
        });
        clearError('editing_post_production_description');
    };

    const handleEditingDescriptionChange = (e) => {
        setFormData({
            ...formData,
            editing_post_production_description: e.target.value
        });
        clearError('editing_post_production_description');
    };

    const handleSoundChange = (e) => {
        setFormData({
            ...formData,
            sound_voice_music: e.target.checked.toString()
        });
        clearError('sound_voice_music_description');
    };

    const handleSoundDescriptionChange = (e) => {
        setFormData({
            ...formData,
            sound_voice_music_description: e.target.value
        });
        clearError('sound_voice_music_description');
    };

    const handleOtherChange = (e) => {
        setFormData({
            ...formData,
            other: e.target.checked.toString()
        });
        clearError('other_description');
    };

    const handleOtherDescriptionChange = (e) => {
        setFormData({
            ...formData,
            other_description: e.target.value
        });
        clearError('other_description');
    };

    return (
        <>
            <div className="cmot-phone">
                <h5>STEP 4</h5>
                <p>
                    AI Stages Used in Production <span className="text-danger">*</span>
                </p>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Tick all applicable stages and briefly describe how AI was used in each (50-200 words per description)
                </Typography>
            </div>

            <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: '#1c5768' }}>
                        Production Stages
                    </Typography>

                    {/* Concept / Script Development */}
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={getCheckboxValue(formData.concept_script_development)}
                                    onChange={handleConceptChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Concept / Script Development
                                </Typography>
                            }
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            How AI was used in concept creation, script writing, or story development
                        </Typography>

                        {getCheckboxValue(formData.concept_script_development) && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                                <TextField
                                    variant="outlined"
                                    label="Description for Concept / Script Development"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    required
                                    value={formData.concept_script_development_description || ''}
                                    onChange={handleConceptDescriptionChange}
                                    error={!!errors?.concept_script_development_description}
                                    helperText={
                                        errors?.concept_script_development_description ? (
                                            errors.concept_script_development_description
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color={getWordCountText(formData.concept_script_development_description || '').color}
                                            >
                                                {getWordCountText(formData.concept_script_development_description || '').text}
                                            </Typography>
                                        )
                                    }
                                    placeholder="Describe the specific AI tools and techniques used in concept/script development..."
                                />
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Storyboarding / Pre-visualization */}
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={getCheckboxValue(formData.storyboarding_pre_visualization)}
                                    onChange={handleStoryboardingChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Storyboarding / Pre-visualization
                                </Typography>
                            }
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            AI usage in storyboarding, shot planning, or visual pre-production
                        </Typography>

                        {getCheckboxValue(formData.storyboarding_pre_visualization) && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                                <TextField
                                    variant="outlined"
                                    label="Description for Storyboarding / Pre-visualization"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    required
                                    value={formData.storyboarding_pre_visualization_description || ''}
                                    onChange={handleStoryboardingDescriptionChange}
                                    error={!!errors?.storyboarding_pre_visualization_description}
                                    helperText={
                                        errors?.storyboarding_pre_visualization_description ? (
                                            errors.storyboarding_pre_visualization_description
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color={getWordCountText(formData.storyboarding_pre_visualization_description || '').color}
                                            >
                                                {getWordCountText(formData.storyboarding_pre_visualization_description || '').text}
                                            </Typography>
                                        )
                                    }
                                    placeholder="Describe the specific AI tools and techniques used in storyboarding/pre-visualization..."
                                />
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Video Generation / Animation */}
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={getCheckboxValue(formData.video_generation_animation)}
                                    onChange={handleVideoChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Video Generation / Animation
                                </Typography>
                            }
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            AI tools for video creation, animation, or motion graphics
                        </Typography>

                        {getCheckboxValue(formData.video_generation_animation) && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                                <TextField
                                    variant="outlined"
                                    label="Description for Video Generation / Animation"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    required
                                    value={formData.video_generation_animation_description || ''}
                                    onChange={handleVideoDescriptionChange}
                                    error={!!errors?.video_generation_animation_description}
                                    helperText={
                                        errors?.video_generation_animation_description ? (
                                            errors.video_generation_animation_description
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color={getWordCountText(formData.video_generation_animation_description || '').color}
                                            >
                                                {getWordCountText(formData.video_generation_animation_description || '').text}
                                            </Typography>
                                        )
                                    }
                                    placeholder="Describe the specific AI tools and techniques used in video generation/animation..."
                                />
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Editing / Post-production */}
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={getCheckboxValue(formData.editing_post_production)}
                                    onChange={handleEditingChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Editing / Post-production
                                </Typography>
                            }
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            AI applications in editing, color grading, VFX, or post-processing
                        </Typography>

                        {getCheckboxValue(formData.editing_post_production) && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                                <TextField
                                    variant="outlined"
                                    label="Description for Editing / Post-production"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    required
                                    value={formData.editing_post_production_description || ''}
                                    onChange={handleEditingDescriptionChange}
                                    error={!!errors?.editing_post_production_description}
                                    helperText={
                                        errors?.editing_post_production_description ? (
                                            errors.editing_post_production_description
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color={getWordCountText(formData.editing_post_production_description || '').color}
                                            >
                                                {getWordCountText(formData.editing_post_production_description || '').text}
                                            </Typography>
                                        )
                                    }
                                    placeholder="Describe the specific AI tools and techniques used in editing/post-production..."
                                />
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Sound / Voice / Music */}
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={getCheckboxValue(formData.sound_voice_music)}
                                    onChange={handleSoundChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Sound / Voice / Music
                                </Typography>
                            }
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            AI usage for audio, voice synthesis, music composition, or sound design
                        </Typography>

                        {getCheckboxValue(formData.sound_voice_music) && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                                <TextField
                                    variant="outlined"
                                    label="Description for Sound / Voice / Music"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    required
                                    value={formData.sound_voice_music_description || ''}
                                    onChange={handleSoundDescriptionChange}
                                    error={!!errors?.sound_voice_music_description}
                                    helperText={
                                        errors?.sound_voice_music_description ? (
                                            errors.sound_voice_music_description
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color={getWordCountText(formData.sound_voice_music_description || '').color}
                                            >
                                                {getWordCountText(formData.sound_voice_music_description || '').text}
                                            </Typography>
                                        )
                                    }
                                    placeholder="Describe the specific AI tools and techniques used in sound/voice/music..."
                                />
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Other */}
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={getCheckboxValue(formData.other)}
                                    onChange={handleOtherChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Other (please specify)
                                </Typography>
                            }
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            Any other AI applications not covered above
                        </Typography>

                        {getCheckboxValue(formData.other) && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                                <TextField
                                    variant="outlined"
                                    label="Description for Other Applications"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    required
                                    value={formData.other_description || ''}
                                    onChange={handleOtherDescriptionChange}
                                    error={!!errors?.other_description}
                                    helperText={
                                        errors?.other_description ? (
                                            errors.other_description
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color={getWordCountText(formData.other_description || '').color}
                                            >
                                                {getWordCountText(formData.other_description || '').text}
                                            </Typography>
                                        )
                                    }
                                    placeholder="Describe any other AI applications not covered above..."
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Validation message */}
                    {errors?.ai_stages_common && (
                        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                            {errors.ai_stages_common}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                💡 Select all production stages where AI tools were significantly used in your project.
            </Typography>
        </>
    );
};

export default Aistages;